import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';
import { setupAxiosInterceptors, setupActivityDetection, isMonitoringRoute } from '../utils/axiosConfig';
import { rbacApi } from '../services/rbacApi';

interface User {
  id: number;
  email: string;
  username?: string | null;
  /** Cédula o documento (aparece en certificados de descarga) */
  nationalId?: string | null;
  /** Nombre del rol en users_roles (el acceso a Seguridad va por allowedModules.seguridad o nombre legado Administrador) */
  role?: string;
  roleId?: number | null;
  allowedModules?: string[];
  /** Permisos `moduleKey:actionKey` (sesiones nuevas / API me/module-keys). */
  allowedActions?: string[];
  /** Máximo de horas hacia atrás que el usuario puede previsualizar grabaciones (null = sin restricción). */
  recordingMaxHoursBack?: number | null;
  firstName?: string;
  lastName?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

/**
 * Minutos de inactividad antes de cerrar sesión automáticamente.
 * Config: REACT_APP_SESSION_TIMEOUT_MINUTES en frontend/.env (default 30 si se
 * omite o es inválido; se limita a un máximo de 24h). No aplica en rutas de
 * monitoreo (ver MONITORING_ROUTES en axiosConfig).
 */
function readSessionTimeoutMinutes(): number {
  const minutes = parseInt(String(process.env.REACT_APP_SESSION_TIMEOUT_MINUTES ?? '').trim(), 10);
  return Number.isFinite(minutes) && minutes > 0 ? Math.min(minutes, 24 * 60) : 30;
}

const SESSION_TIMEOUT_MINUTES = readSessionTimeoutMinutes();

function getInitialAuth() {
  try {
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (savedToken && savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
      return { token: savedToken, user: JSON.parse(savedUser) as User };
    }
  } catch {}
  return { token: null, user: null };
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(() => getInitialAuth().user);
  const [token, setToken] = useState<string | null>(() => getInitialAuth().token);

  const handleLogout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    window.location.href = '/login';
  };

  useEffect(() => {
    // Configurar interceptores de axios para refresh automático
    setupAxiosInterceptors(handleLogout);

    // Configurar detección de inactividad (REACT_APP_SESSION_TIMEOUT_MINUTES, default 30 min).
    // No cerrar sesión en Stream, Grabaciones, certificados de descarga ni Mapas (monitoreo / revisión pasiva).
    const cleanupActivity = setupActivityDetection(handleLogout, SESSION_TIMEOUT_MINUTES, isMonitoringRoute);

    // Recuperar token del localStorage al cargar
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (savedToken && savedUser) {
      try {
        // Evita crashear si algún deploy previo guardó "undefined"/"null" como string
        if (savedUser === 'undefined' || savedUser === 'null') {
          throw new Error(`Invalid stored user: ${savedUser}`);
        }
        const parsed = JSON.parse(savedUser) as User;
        setToken(savedToken);
        setUser(parsed);
        axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
      } catch {
        // Storage corrupto -> limpiar y continuar sin sesión
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        delete axios.defaults.headers.common['Authorization'];
      }
    }

    return () => {
      cleanupActivity();
    };
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email,
        password,
      });

      // backend_api wraps payload under `data` and uses camelCase tokens
      const payload = response.data?.data;
      const access_token = payload?.accessToken;
      const refresh_token = payload?.refreshToken;
      const userData = payload?.user;
      if (!access_token || !userData) {
        throw new Error('Respuesta de login inválida');
      }
      
      setToken(access_token);
      setUser(userData);
      
      localStorage.setItem('token', access_token);
      if (refresh_token) localStorage.setItem('refreshToken', refresh_token);
      localStorage.setItem('user', JSON.stringify(userData));
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return userData;
    } catch (error: any) {
      const apiError = error.response?.data?.error;
      const msg =
        apiError?.message ||
        error.response?.data?.message ||
        error.message ||
        'Error al iniciar sesión';
      const err = new Error(msg) as Error & {
        code?: string;
        details?: Record<string, unknown> | null;
      };
      err.code = apiError?.code;
      err.details = apiError?.details ?? null;
      throw err;
    }
  };

  const logout = () => {
    handleLogout();
  };

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...userData };
      localStorage.setItem('user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  useEffect(() => {
    const onRbac = (e: Event) => {
      const detail = (e as CustomEvent<{ allowedModules?: string[]; allowedActions?: string[] }>).detail;
      if (!detail || typeof detail !== 'object') return;
      setUser((prev) => {
        if (!prev) return prev;
        const updated = { ...prev };
        if (Array.isArray(detail.allowedModules)) {
          updated.allowedModules = detail.allowedModules;
        }
        if (Array.isArray(detail.allowedActions)) {
          updated.allowedActions = detail.allowedActions;
        }
        localStorage.setItem('user', JSON.stringify(updated));
        return updated;
      });
    };
    window.addEventListener('vms:rbac-update', onRbac);
    return () => window.removeEventListener('vms:rbac-update', onRbac);
  }, []);

  useEffect(() => {
    if (!token || !user) return;
    const needModules = user.allowedModules === undefined;
    const needActions = user.allowedActions === undefined;
    const needMaxHours = user.recordingMaxHoursBack === undefined;
    if (!needModules && !needActions && !needMaxHours) return;
    let cancelled = false;
    rbacApi
      .getMyEffectiveRbac()
      .then(({ moduleKeys, actionGrants, recordingMaxHoursBack }) => {
        if (!cancelled) {
          setUser((prev) => {
            if (!prev) return prev;
            const updated = {
              ...prev,
              ...(needModules ? { allowedModules: moduleKeys } : {}),
              ...(needActions ? { allowedActions: actionGrants } : {}),
              ...(needMaxHours ? { recordingMaxHoursBack } : {})
            };
            localStorage.setItem('user', JSON.stringify(updated));
            return updated;
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setUser((prev) => {
            if (!prev) return prev;
            const updated = {
              ...prev,
              ...(needModules ? { allowedModules: [] } : {}),
              ...(needActions ? { allowedActions: [] } : {}),
              ...(needMaxHours ? { recordingMaxHoursBack: null } : {})
            };
            localStorage.setItem('user', JSON.stringify(updated));
            return updated;
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, user?.id, user?.role, user?.allowedModules, user?.allowedActions, user?.recordingMaxHoursBack]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        updateUser,
        isAuthenticated: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
